/**
 * =============================================================================
 * Página: ReferralProgramDetail
 * Versão: 1.0.0
 *
 * Descrição:
 *   Tela de detalhe de um Programa de Indicação. Reaproveita as MESMAS páginas
 *   globais (Campanhas, Afiliados, Materiais) em modo `embedded`, escopadas ao
 *   `programId` da URL. Não duplica tabelas, hooks nem RPCs.
 *
 *   Abas: Dashboard · Campanhas · Afiliados · Materiais
 *   - A aba ativa é sincronizada com a query string `?tab=`.
 *
 * RELEASE: 2026-04-25 v1.0.0 - Criação do shell de detalhe do programa.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Award, BarChart3, Megaphone, Users, ImageIcon, Link2, ExternalLink, Copy, Mail, Pencil } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { useReferralCampaigns } from '@/hooks/useReferralCampaigns';
import { useReferralMaterials } from '@/hooks/useReferralMaterials';
import ReferralCampaigns from './ReferralCampaigns';
import ReferralAffiliates from './ReferralAffiliates';
import ReferralMaterials from './ReferralMaterials';
import { AffiliateInvitesTab } from '@/components/referral/AffiliateInvitesTab';
import { ReferralProgramFormModal } from '@/components/referral/ReferralProgramFormModal';

const PROGRAM_TYPE_LABEL: Record<string, string> = {
  invite_only: 'Privado',
  open: 'Público',
  internal: 'Interno',
};

const VALID_TABS = ['dashboard', 'campanhas', 'afiliados', 'materiais', 'convites'] as const;
type TabKey = (typeof VALID_TABS)[number];

export default function ReferralProgramDetail() {
  const { programId = '' } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTenant } = useAuth();
  const { toast } = useToast();
  const tenantSlug = currentTenant?.slug ?? '';
  const [editOpen, setEditOpen] = useState(false);

  const tabParam = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey =
    tabParam && (VALID_TABS as readonly string[]).includes(tabParam) ? tabParam : 'dashboard';

  const { data: programs = [], isLoading } = useReferralPrograms();
  const program = useMemo(
    () => programs.find((p) => p.id === programId) ?? null,
    [programs, programId],
  );

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  // URL pública de cadastro (apenas para programas "open")
  const publicSignupUrl =
    program && program.program_type === 'open' && tenantSlug && typeof window !== 'undefined'
      ? `${window.location.origin}/afiliado/cadastro/${tenantSlug}/${program.slug}`
      : null;

  const handleCopyPublicLink = async () => {
    if (!publicSignupUrl) return;
    try {
      await navigator.clipboard.writeText(publicSignupUrl);
      toast({ title: 'Link de cadastro copiado!' });
    } catch {
      toast({ title: 'Não foi possível copiar o link', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {/* Header contextual do programa */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => navigate('/tenant/indicacao/programas')}
            title="Voltar para Programas"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Award className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            {isLoading ? (
              <Skeleton className="h-5 w-48" />
            ) : program ? (
              <>
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <h1 className="text-lg font-semibold truncate">{program.name}</h1>
                  {/* Status no padrão Departamentos (Badge outline + StatusDot). */}
                  <Badge
                    variant="outline"
                    className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                  >
                    <StatusDot tone={program.is_active ? 'emerald' : 'muted'} />
                    {program.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                  <Badge variant="outline" className="hidden sm:inline-flex">
                    {PROGRAM_TYPE_LABEL[program.program_type] ?? program.program_type}
                  </Badge>
                  {/* Atalho Editar — abre modal reaproveitando ReferralProgramFormModal. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() => setEditOpen(true)}
                    title="Editar programa"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Editar</span>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  /{program.slug}
                  {program.description ? ` · ${program.description}` : ''}
                </p>
              </>
            ) : (
              <h1 className="text-lg font-semibold">Programa não encontrado</h1>
            )}
          </div>
        </div>

        {/*
          v1.1.0 — Substitui o card "fora a fora" por dois botões discretos
          (ghost) no header. Apenas para programas "open" + ativos com URL
          pública resolvida. Mantém ações de copiar e abrir, agora compactas.
        */}
        {program && program.program_type === 'open' && program.is_active && publicSignupUrl && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCopyPublicLink}
              className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              title={publicSignupUrl}
            >
              <Link2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Copiar link de cadastro</span>
              <span className="inline sm:hidden">Copiar link</span>
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() =>
                window.open(publicSignupUrl, '_blank', 'noopener,noreferrer')
              }
              title="Abrir página de cadastro"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Abas — sincronizadas com a query string.
          A aba "Convites" só aparece para programas do tipo invite_only. */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        {/* Abas no padrão underline (referência: WlFinancial do painel WL). */}
        <TabsList className="bg-transparent border-b h-auto p-0 gap-6 w-full justify-start rounded-none">
          <TabsTrigger
            value="dashboard"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <BarChart3 className="h-4 w-4 mr-2" /> Dashboard
          </TabsTrigger>
          <TabsTrigger
            value="campanhas"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <Megaphone className="h-4 w-4 mr-2" /> Campanhas
          </TabsTrigger>
          <TabsTrigger
            value="afiliados"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <Users className="h-4 w-4 mr-2" /> Afiliados
          </TabsTrigger>
          <TabsTrigger
            value="materiais"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <ImageIcon className="h-4 w-4 mr-2" /> Materiais
          </TabsTrigger>
          {program?.program_type === 'invite_only' && (
            <TabsTrigger
              value="convites"
              className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
            >
              <Mail className="h-4 w-4 mr-2" /> Convites
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <ProgramDashboardTab programId={programId} />
        </TabsContent>

        <TabsContent value="campanhas" className="mt-4">
          <ReferralCampaigns embedded embeddedProgramId={programId} />
        </TabsContent>

        <TabsContent value="afiliados" className="mt-4">
          <ReferralAffiliates embedded embeddedProgramId={programId} />
        </TabsContent>

        <TabsContent value="materiais" className="mt-4">
          <ReferralMaterials embedded embeddedProgramId={programId} />
        </TabsContent>

        {program?.program_type === 'invite_only' && (
          <TabsContent value="convites" className="mt-4">
            <AffiliateInvitesTab programId={programId} />
          </TabsContent>
        )}
      </Tabs>

      {/* Modal de edição — reaproveitamento do formulário existente. */}
      {program && (
        <ReferralProgramFormModal
          open={editOpen}
          onOpenChange={setEditOpen}
          program={program}
        />
      )}
    </div>
  );
}

/**
 * Aba Dashboard — placeholder enxuto reaproveitando os mesmos hooks das demais
 * abas. TanStack Query deduplica as queries, então não há custo extra.
 *
 * Quando o usuário pedir métricas mais ricas, criamos uma RPC dedicada
 * `get_referral_program_dashboard` em iteração separada.
 */
function ProgramDashboardTab({ programId }: { programId: string }) {
  const { data: campaigns = [], isLoading: loadingCampaigns } =
    useReferralCampaigns(programId);
  const { data: materials = [], isLoading: loadingMaterials } = useReferralMaterials({
    programId,
  });

  const activeCampaigns = campaigns.filter((c) => c.is_active).length;
  const activeMaterials = materials.filter((m) => m.is_active).length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <KpiTile
        icon={Megaphone}
        label="Campanhas"
        value={loadingCampaigns ? '…' : campaigns.length}
        hint={`${activeCampaigns} ativas`}
      />
      <KpiTile
        icon={ImageIcon}
        label="Materiais"
        value={loadingMaterials ? '…' : materials.length}
        hint={`${activeMaterials} ativos`}
      />
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Métricas detalhadas (cliques, conversões e comissões) por programa
          serão exibidas aqui em uma próxima iteração.
        </CardContent>
      </Card>
    </div>
  );
}

function KpiTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-xl font-semibold leading-tight">{value}</div>
          {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
